import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";

/**
 * Phase 2 community curation. A "signal" is a per-message, per-user mark
 * (helpful / worth_reading / best_answer). Channel + workspace are DERIVED
 * from the message (never trusted from the caller — Pattern 10), and the
 * actor must be an active member of the message's channel (Pattern 6). The
 * UI toggles by checking existing signals first, then insert-or-delete, so
 * the `(message_id, user_id, signal_type)` unique index never conflicts.
 *
 * Signals are PUBLIC-workspace-only (they feed portable reputation, which the
 * loader aggregates from is_public workspaces) — the server gate mirrors the
 * thread-qa public-workspace gate (#1931).
 */
export const communitySignalsMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      message_id: z.string(),
      signal_type: z.enum(["helpful", "worth_reading", "best_answer"]),
      created_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (!ctx?.sub) throw new Error("Unauthorized: missing caller identity");
      const userId = ctx.sub;
      const msg = await tx.run(zql.messages.where("id", args.message_id).one());
      const channel = msg
        ? await tx.run(zql.channels.where("id", msg.channel_id).one())
        : null;

      // Community signals are a workspace-channel curation feature. DMs have
      // no workspace, so reject rather than persist a bogus empty
      // workspace_id (the channel is cached client-side because the user is
      // viewing it; the server re-checks authoritatively below).
      if (channel && !channel.workspace_id) {
        throw new Error("Signals are not available in direct messages");
      }
      if (args.signal_type === "best_answer") {
        throw new Error("Use the thread answer controls to accept an answer");
      }

      // No self-curation. A user can't signal their own message — this keeps
      // the visible per-message count consistent with portable reputation
      // (which already excludes self-votes via `cs.user_id <> m.user_id`) and
      // stops an author inflating their own score. `msg` is cached client-side
      // (the user is viewing it), so this also blocks the optimistic write.
      if (msg && msg.user_id === userId) {
        throw new Error("You can't mark your own message");
      }

      if (tx.location === "server") {
        if (!msg) throw new Error("Message not found");
        if (!channel?.workspace_id) {
          throw new Error("Signals are not available in direct messages");
        }
        const membership = await tx.run(
          zql.channel_members
            .where("channel_id", msg.channel_id)
            .where("user_id", userId)
            .where("removed_at", "IS", null)
            .one(),
        );
        if (!membership) throw new Error("Unauthorized: not a channel member");
        // Community signals power public-community reputation (the loader only
        // counts signals from is_public workspaces). Gate to public workspaces
        // — re-checked here for a mid-flight is_public toggle — mirroring the
        // thread-qa gate (#1931) so the curation perimeter is consistent.
        const ws = await tx.run(
          zql.workspaces.where("id", channel.workspace_id).one(),
        );
        if (!ws?.is_public) {
          throw new Error(
            "Community signals are only available in public workspaces",
          );
        }
        await tx.mutate.community_signals.upsert({
          id: args.id,
          workspace_id: channel.workspace_id,
          channel_id: msg.channel_id,
          message_id: args.message_id,
          user_id: userId,
          signal_type: args.signal_type,
          created_at: args.created_at,
        });
        return;
      }

      // Client optimistic pass — derive the workspace from the cached channel;
      // the server replay fills the authoritative value.
      await tx.mutate.community_signals.upsert({
        id: args.id,
        workspace_id: channel?.workspace_id ?? "",
        channel_id: msg?.channel_id ?? "",
        message_id: args.message_id,
        user_id: userId,
        signal_type: args.signal_type,
        created_at: args.created_at,
      });
    },
  ),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const row = await tx.run(
          zql.community_signals.where("id", args.id).one(),
        );
        if (!row) return;
        if (row.user_id !== ctx?.sub) {
          throw new Error("Unauthorized: not owner");
        }
      }
      await tx.mutate.community_signals.delete({ id: args.id });
    },
  ),
};
